/**
 * SharedEstimateViewContext
 *
 * Bridge between the SharedEstimate page (which loads an estimate by GUID
 * from the API) and the app-level Header (which lives above <Routes /> and
 * can't see SharedEstimate's local state directly).
 *
 * SharedEstimate publishes its currently-viewed estimate here on mount;
 * Header consumes it to augment the menu on /estimate/:id routes ("Save
 * Current Config" saves the viewed estimate, not whatever wizard state is
 * lingering in EstimatorContext; "New Estimate from this template" clones
 * the config minus personal info).
 *
 * Must wrap both <Header /> and <Routes />.
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { WizardState } from '../types';

export interface SharedEstimateView {
  /** Wizard config snapshot loaded from the saved estimate. */
  wizardState: WizardState;
  /** Display name stored alongside the estimate. */
  estimateName: string;
}

interface SharedEstimateViewContextValue {
  view: SharedEstimateView | null;
  setView: (view: SharedEstimateView | null) => void;
}

const SharedEstimateViewContext = createContext<SharedEstimateViewContextValue | undefined>(undefined);

export const SharedEstimateViewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [view, setViewState] = useState<SharedEstimateView | null>(null);

  const setView = useCallback((next: SharedEstimateView | null) => {
    setViewState(next);
  }, []);

  const value = useMemo(() => ({ view, setView }), [view, setView]);

  return (
    <SharedEstimateViewContext.Provider value={value}>
      {children}
    </SharedEstimateViewContext.Provider>
  );
};

export function useSharedEstimateView(): SharedEstimateViewContextValue {
  const ctx = useContext(SharedEstimateViewContext);
  if (!ctx) {
    throw new Error('useSharedEstimateView must be used within a SharedEstimateViewProvider');
  }
  return ctx;
}
